import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Center, Loader, Stack, Text, Title } from '@mantine/core';

import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
import LogRocket from 'logrocket';

import { apiClient } from '@/services/legacy-api';
import { User } from '@/types';

export function AuthCallbackPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const userData = await apiClient.getCurrentUser();
        const user = userData[0] ?? userData;

        const userInfo: User = {
          id: user.id ?? user._id,
          email: user.email,
          firstName: user.first_name ?? user.email,
          lastName: user.last_name,
          isClient: user.is_client,
          isNetworkPartner: user.is_network_partner,
          isAdministrator: user.is_administrator,
        };

        // Update monitoring services with user info
        LogRocket.identify(userInfo.id, {
          email: userInfo.email,
          firstName: userInfo.firstName || '',
          lastName: userInfo.lastName || '',
          isClient: userInfo.isClient || false,
          isNetworkPartner: userInfo.isNetworkPartner || false,
          isAdministrator: userInfo.isAdministrator || false,
        });

        Sentry.setUser({
          id: userInfo.id,
          email: userInfo.email,
          username: `${userInfo.firstName} ${userInfo.lastName}`,
        });

        // Navigate to home page
        navigate('/', { replace: true });
      } catch (err: any) {
        // // Clear cookies as well in case of error
        const cookieOptions = { domain: '.localhost' };
        Cookies.remove('expiry', cookieOptions);

        const errorMessage = err.response?.data?.error ?? err.message ?? 'Authentication failed';
        setError(errorMessage);

        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Title order={3}>Completing authentication...</Title>
          <Text c="dimmed">Please wait while we sign you in</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Title order={3} c="red">
            Authentication Error
          </Title>
          <Text c="dimmed" ta="center">
            {error}
          </Text>
          <Text size="sm" c="dimmed">
            Redirecting to login...
          </Text>
        </Stack>
      </Center>
    );
  }

  return null;
}
